import type { OverlayLayout, OverlayObjectTransform } from "@impleotv-pm/st-overlay";

export const PLAYER_OVERLAY_LAYOUT_STORAGE_KEY = "sky-view.player.overlay.layout";

export type OverlayViewport = {
  width: number;
  height: number;
};

export type StoredPlayerOverlayLayout = OverlayLayout;
type HeadingDisplay = OverlayLayout["headingDisplay"];
type RulerPoints = NonNullable<OverlayLayout["rulerPoints"]>;
type RulerPoint = NonNullable<RulerPoints["start"]>;

type LegacyEditorSession = {
  layout?: OverlayLayout | null;
  texts?: unknown[];
};

const getSafeViewport = (viewport: OverlayViewport) => ({
  width: Math.max(1, viewport.width),
  height: Math.max(1, viewport.height),
  minDimension: Math.max(1, Math.min(viewport.width, viewport.height)),
});

const normalizeCoordinate = (value: number | undefined, total: number) =>
  typeof value === "number" && Number.isFinite(value) ? value / total : value;

const denormalizeCoordinate = (value: number | undefined, total: number) =>
  typeof value === "number" && Number.isFinite(value) ? value * total : value;

const isNormalizedCoordinate = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;

const normalizeTransform = (
  value: OverlayObjectTransform,
  viewport: ReturnType<typeof getSafeViewport>,
): OverlayObjectTransform => ({
  ...value,
  x: value.x / viewport.width,
  y: value.y / viewport.height,
  width: normalizeCoordinate(value.width, viewport.width),
  height: normalizeCoordinate(value.height, viewport.height),
  fontSize: normalizeCoordinate(value.fontSize, viewport.minDimension),
});

const denormalizeTransform = (
  value: OverlayObjectTransform,
  viewport: ReturnType<typeof getSafeViewport>,
): OverlayObjectTransform => ({
  ...value,
  x: value.x * viewport.width,
  y: value.y * viewport.height,
  width: denormalizeCoordinate(value.width, viewport.width),
  height: denormalizeCoordinate(value.height, viewport.height),
  fontSize: denormalizeCoordinate(value.fontSize, viewport.minDimension),
});

const normalizeHeadingDisplay = (
  value: HeadingDisplay,
  viewport: ReturnType<typeof getSafeViewport>,
): HeadingDisplay => ({
  ...value,
  x: value.x / viewport.width,
  y: value.y / viewport.height,
  // Compass size is serialized by st-overlay as the rendered width, so keep it
  // proportional to viewport width to avoid drift between editor/viewer surfaces.
  size: normalizeCoordinate(value.size, viewport.width),
  fontSize: normalizeCoordinate(value.fontSize, viewport.minDimension),
});

const denormalizeHeadingDisplay = (
  value: HeadingDisplay,
  viewport: ReturnType<typeof getSafeViewport>,
): HeadingDisplay => ({
  ...value,
  x: value.x * viewport.width,
  y: value.y * viewport.height,
  size: denormalizeCoordinate(value.size, viewport.width),
  fontSize: denormalizeCoordinate(value.fontSize, viewport.minDimension),
});

const normalizeRulerPoint = (
  value: RulerPoint | null | undefined,
  viewport: ReturnType<typeof getSafeViewport>,
): RulerPoint | null => {
  if (!value) {
    return null;
  }

  return {
    x: value.x / viewport.width,
    y: value.y / viewport.height,
  };
};

const denormalizeRulerPoint = (
  value: RulerPoint | null | undefined,
  viewport: ReturnType<typeof getSafeViewport>,
): RulerPoint | null => {
  if (!value) {
    return null;
  }

  if (isNormalizedCoordinate(value.x) && isNormalizedCoordinate(value.y)) {
    return {
      x: value.x * viewport.width,
      y: value.y * viewport.height,
    };
  }

  return { ...value };
};

const normalizeRulerPoints = (
  value: OverlayLayout["rulerPoints"],
  viewport: ReturnType<typeof getSafeViewport>,
): OverlayLayout["rulerPoints"] => {
  if (!value) {
    return value;
  }

  return {
    start: normalizeRulerPoint(value.start, viewport),
    end: normalizeRulerPoint(value.end, viewport),
  };
};

const denormalizeRulerPoints = (
  value: OverlayLayout["rulerPoints"],
  viewport: ReturnType<typeof getSafeViewport>,
): OverlayLayout["rulerPoints"] => {
  if (!value) {
    return value;
  }

  return {
    start: denormalizeRulerPoint(value.start, viewport),
    end: denormalizeRulerPoint(value.end, viewport),
  };
};

const normalizeStoredLayout = (
  layout: OverlayLayout | null | undefined,
): OverlayLayout | null => {
  if (!layout) {
    return null;
  }

  return {
    ...layout,
    placeholder: null,
    dataTexts: Array.isArray(layout.dataTexts) ? layout.dataTexts : [],
  };
};

export const normalizePlayerOverlayLayout = (
  layout: OverlayLayout,
  viewport: OverlayViewport,
): StoredPlayerOverlayLayout => {
  const safeViewport = getSafeViewport(viewport);

  return {
    ...layout,
    placeholder: null,
    headingDisplay: normalizeHeadingDisplay(layout.headingDisplay, safeViewport),
    vmti: layout.vmti.map((value) => normalizeTransform(value, safeViewport)),
    texts: layout.texts.map((value) => normalizeTransform(value, safeViewport)),
    dataTexts: (layout.dataTexts ?? []).map((value) => ({
      ...value,
      x: value.x / safeViewport.width,
      y: value.y / safeViewport.height,
      fontSize: normalizeCoordinate(value.fontSize, safeViewport.minDimension),
    })),
    rulerPoints: normalizeRulerPoints(layout.rulerPoints, safeViewport),
  };
};

export const denormalizePlayerOverlayLayout = (
  layout: StoredPlayerOverlayLayout,
  viewport: OverlayViewport,
): OverlayLayout => {
  const safeViewport = getSafeViewport(viewport);

  return {
    ...layout,
    placeholder: null,
    headingDisplay: denormalizeHeadingDisplay(layout.headingDisplay, safeViewport),
    vmti: layout.vmti.map((value) => denormalizeTransform(value, safeViewport)),
    texts: layout.texts.map((value) => denormalizeTransform(value, safeViewport)),
    dataTexts: (layout.dataTexts ?? []).map((value) => ({
      ...value,
      x: value.x * safeViewport.width,
      y: value.y * safeViewport.height,
      fontSize: denormalizeCoordinate(value.fontSize, safeViewport.minDimension),
    })),
    rulerPoints: denormalizeRulerPoints(layout.rulerPoints, safeViewport),
  };
};

export const createDefaultRulerPoints = (viewport: OverlayViewport): RulerPoints => {
  const safeViewport = getSafeViewport(viewport);
  const centerY = safeViewport.height * 0.5;

  return {
    start: {
      x: safeViewport.width * 0.3,
      y: centerY,
    },
    end: {
      x: safeViewport.width * 0.7,
      y: centerY,
    },
  };
};

export const readPlayerOverlayLayout = (): StoredPlayerOverlayLayout | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(PLAYER_OVERLAY_LAYOUT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as OverlayLayout | LegacyEditorSession | null;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    if ("layout" in parsed) {
      return normalizeStoredLayout((parsed as LegacyEditorSession).layout) ?? null;
    }

    return normalizeStoredLayout(parsed as OverlayLayout);
  } catch (_error) {
    return null;
  }
};

export const persistPlayerOverlayLayout = (value: StoredPlayerOverlayLayout | null) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!value) {
      window.localStorage.removeItem(PLAYER_OVERLAY_LAYOUT_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(PLAYER_OVERLAY_LAYOUT_STORAGE_KEY, JSON.stringify(value));
  } catch (_error) {
    // Ignore storage failures in restricted browser environments.
  }
};
